import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/ui/password-input";
import { formatApiError } from "../lib/api";
import api from "../lib/api";
import CheckEmail from "./CheckEmail";

export default function Register() {
  const { register } = useAuth();
  const [search] = useSearchParams();
  const inviteToken = search.get("invite");
  const inviteEmail = search.get("email") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState(inviteEmail);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingVerify, setPendingVerify] = useState(null); // {email, emailSent}
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await register(email, password, name);
      if (res?.verification_required) {
        setPendingVerify({ email: res.email || email, emailSent: res.email_sent !== false });
        return;
      }
      // Legacy / fallback path — server didn't gate the registration.
      if (inviteToken) {
        try {
          const { data } = await api.post(`/invites/${inviteToken}/accept`);
          navigate(`/programs/${data.program_id}`);
          return;
        } catch {
          navigate(`/invite/${inviteToken}`);
          return;
        }
      }
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (pendingVerify) {
    return <CheckEmail email={pendingVerify.email} emailSent={pendingVerify.emailSent} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-white">
      <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="register-form">
        <p className="label-overline">Create account</p>
        <h2 className="font-heading text-3xl font-semibold mt-2">Start reporting with Array Engine</h2>
        <p className="text-sm text-slate-500 mt-1">
          {inviteToken
            ? "Finish creating your account to accept the program invite."
            : "A clean, evidence-driven workspace for your M&E team."}
        </p>

        <div className="mt-8 space-y-4">
          <div>
            <Label htmlFor="name" className="label-overline">Full name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-2" data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="email" className="label-overline">Email</Label>
            <Input
              id="email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              required readOnly={Boolean(inviteToken && inviteEmail)}
              className={`mt-2 ${inviteToken && inviteEmail ? "bg-slate-50 text-slate-700" : ""}`}
              data-testid="register-email-input"
            />
            {inviteToken && inviteEmail && (
              <p className="text-[11px] text-slate-500 mt-1">Locked to the invited email — sign up with this address to join the program.</p>
            )}
          </div>
          <div>
            <Label htmlFor="password" className="label-overline">Password</Label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="At least 8 characters"
              className="mt-2"
              testid="register-password-input"
            />
          </div>
          {error && (
            <div className="border border-red-200 bg-red-50 text-red-700 text-sm p-3 rounded-sm" data-testid="register-error">
              {error}
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="register-submit-button">
            {loading ? "Creating…" : (inviteToken ? "Create account & join" : "Create account")}
          </Button>
        </div>

        <div className="mt-6 text-sm text-slate-600">
          Already have an account?{" "}
          <Link to={inviteToken ? `/login?invite=${inviteToken}` : "/login"} className="underline underline-offset-4 text-slate-900" data-testid="login-link">Sign in</Link>
        </div>
      </form>
    </div>
  );
}
